import React from 'react';
import {
    Box,
    Container,
    Typography,
    Avatar,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Theme Colors
const bgColor = '#FFF8F2'


const Collaborators = ({ t }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    // Sample data - replace with your actual collaborator data
    const mainCollaborators = [
        { id: 1, logo: '/images/collaborators/main/main1.jpeg' },
        { id: 2, logo: '/images/collaborators/main/main2.jpeg' },
        { id: 3, logo: '/images/collaborators/main/main3.jpeg' },
        { id: 4, logo: '/images/collaborators/main/main4.jpeg' },
        { id: 5, logo: '/images/collaborators/main/main5.jpeg' },
        { id: 6, logo: '/images/collaborators/main/main6.jpeg' },
        { id: 7, logo: '/images/collaborators/main/main7.jpeg' },
    ];

    const normalCollaborators = [
        { id: 9, logo: '/images/collaborators/1.jpeg' },
        { id: 10, logo: '/images/collaborators/2.jpeg' },
        { id: 11, logo: '/images/collaborators/3.jpeg' },
        { id: 12, logo: '/images/collaborators/4.jpeg' },
        { id: 13, logo: '/images/collaborators/5.jpeg' },
        { id: 14, logo: '/images/collaborators/6.jpeg' },
        { id: 15, logo: '/images/collaborators/7.jpeg' },
        { id: 16, logo: '/images/collaborators/8.jpeg' },
        { id: 17, logo: '/images/collaborators/9.jpeg' },
        { id: 18, logo: '/images/collaborators/10.jpeg' },
        { id: 19, logo: '/images/collaborators/11.jpeg' },
        { id: 20, logo: '/images/collaborators/12.jpeg' },
        { id: 21, logo: '/images/collaborators/13.jpeg' },
        { id: 22, logo: '/images/collaborators/14.jpeg' },
        { id: 23, logo: '/images/collaborators/15.jpeg' },
        { id: 24, logo: '/images/collaborators/16.jpeg' },
        { id: 25, logo: '/images/collaborators/17.jpeg' },
        { id: 26, logo: '/images/collaborators/18.jpeg' },
        { id: 27, logo: '/images/collaborators/19.jpeg' },
        { id: 28, logo: '/images/collaborators/20.jpeg' },
    ];

    // Carousel settings for main collaborators
    const mainCarouselSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: isMobile ? 1 : isTablet ? 2 : 3,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 3000,
        arrows: !isMobile,
        pauseOnHover: true,
        cssEase: 'cubic-bezier(0.645, 0.045, 0.355, 1)'
    };

    // Carousel settings for normal collaborators (infinite scroll)
    const normalCarouselSettings = {
        dots: false,
        arrows: false,
        infinite: true,
        speed: 5000,
        slidesToShow: isMobile ? 3 : isTablet ? 5 : 7,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 0,
        cssEase: 'linear',
        pauseOnHover: false,
        variableWidth: true
    };

    return (
        <Box sx={{
            py: 12,
            // backgroundColor: bgColor,
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            // backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 248, 242, 0.9))',
            // backgroundAttachment: 'fixed',
            // backgroundSize: 'fit'
        }}>

            <Container maxWidth="xl">
                {/* Section Header */}
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography variant="overline" sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        letterSpacing: 1.5,
                        display: 'block',
                        mb: 1
                    }}>
                        {t('ourCollaborators')}
                    </Typography>
                    <Typography variant="h4" component="h2" sx={{
                        fontWeight: 700,
                        mb: 2,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        display: 'inline-block'
                    }}>
                        {t('trustedBy')}
                    </Typography>
                </Box>

                {/* Main Collaborators Carousel */}
                <Box sx={{ mb: 8 }}>
                    <Typography variant="h6" align="center" sx={{
                        mb: 4,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&::before, &::after': {
                            content: '""',
                            flex: 1,
                            height: 1,
                            backgroundColor: theme.palette.divider,
                            mx: 2
                        }
                    }}>
                        {t('mainCollaborators')}
                    </Typography>

                    <Box sx={{ px: isMobile ? 0 : 4 }}>
                        <Slider {...mainCarouselSettings}>
                            {mainCollaborators.map((collaborator) => (
                                <Box key={collaborator.id} sx={{ px: 2, outline: 'none' }}>
                                    <Box
                                        component="a"
                                        rel="noopener noreferrer"
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            textDecoration: 'none',
                                            p: 4,
                                            height: '100%',
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        <Avatar
                                            src={collaborator.logo}
                                            alt={collaborator.name}
                                            sx={{
                                                width: isMobile ? 140 : 180,
                                                height: isMobile ? 140 : 180,
                                                borderRadius: '50%',
                                                mb: 3,
                                                objectFit: 'cover',
                                                backgroundColor: theme.palette.background.paper,
                                                boxShadow: theme.shadows[2]
                                            }}
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </Slider>
                    </Box>
                </Box>

                {/* Normal Collaborators - Infinite Scroll */}
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h6" align="center" sx={{
                        mb: 4,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&::before, &::after': {
                            content: '""',
                            flex: 1,
                            height: 1,
                            backgroundColor: theme.palette.divider,
                            mx: 2
                        }
                    }}>
                        {t('partners')}
                    </Typography>

                    <Box sx={{ overflow: 'hidden' }}>
                        <Slider {...normalCarouselSettings}>
                            {[...normalCollaborators, ...normalCollaborators].map((collaborator, index) => (
                                <Box key={`${collaborator.id}-${index}`} sx={{ px: 2, outline: 'none' }}>
                                    <Box
                                        component="a"
                                        href={collaborator.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            textDecoration: 'none',
                                            p: 3,
                                            height: '100%',
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        <Avatar
                                            src={collaborator.logo}
                                            alt={collaborator.name}
                                            sx={{
                                                width: isMobile ? 80 : 100,
                                                height: isMobile ? 80 : 100,
                                                borderRadius: '50%',
                                                mb: 2,
                                                objectFit: 'contain',
                                                backgroundColor: theme.palette.background.paper,
                                                boxShadow: theme.shadows[1]
                                            }}
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </Slider>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default Collaborators;